import { type ChangeEvent, useCallback } from 'react';
import type { Lens } from '@hookform/lenses';
import { useFormContext, useWatch } from 'react-hook-form';
import { z } from 'zod/v4';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { Typography } from '@interxfi/design-system/components/typography';
import { IsoV1Country, type IxcRegistrationV1AffiliationsPoliticallyExposedPerson } from '@interxfi/types/generated/api';
import { AppCheckboxInputWithLens } from '../../../../../../components/form/app-checkbox-input';
import { AppSelectInputWithLens } from '../../../../../../components/form/app-select-input';
import { buildCountryOptions } from '../../../../../../util/country';

// Non-US only
const countryOptions = buildCountryOptions(
  'en-US',
  Object.values(IsoV1Country).filter((country) => country !== IsoV1Country.Us),
);

export const politicallyExposedPersonFormSchema = z.object({
  isProviding: z.boolean(),
  value: z
    .object({
      country: z.enum(IsoV1Country, { error: 'Country is required' }),
    })
    .optional(),
});

export type PoliticallyExposedPersonFormValues = z.infer<typeof politicallyExposedPersonFormSchema>;

export const defaultPoliticallyExposedPersonFormValues: PoliticallyExposedPersonFormValues = {
  isProviding: false,
};

export function formValuesToPoliticallyExposedPerson(
  values: PoliticallyExposedPersonFormValues,
): IxcRegistrationV1AffiliationsPoliticallyExposedPerson | undefined {
  if (!values.value || !values.isProviding) {
    return undefined;
  }

  return {
    country: values.value.country,
  };
}

export function getInitialFormValuesForPoliticallyExposedPerson(
  politicallyExposedPerson: IxcRegistrationV1AffiliationsPoliticallyExposedPerson | undefined,
): PoliticallyExposedPersonFormValues | undefined {
  if (!politicallyExposedPerson) {
    return defaultPoliticallyExposedPersonFormValues;
  }

  return {
    isProviding: true,
    value: {
      country: politicallyExposedPerson.country,
    },
  };
}

interface PoliticallyExposedPersonFormProps {
  lens: Lens<PoliticallyExposedPersonFormValues>;
}

export function PoliticallyExposedPersonForm({ lens }: PoliticallyExposedPersonFormProps) {
  const { setValue } = useFormContext();
  const { name: valueName } = lens.focus('value').interop();
  const isProviding = useWatch(lens.focus('isProviding').interop());
  const handleAffiliatedPoliticallyExposedPersonToggle = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setValue(valueName, e.target.checked ? { country: '' as IsoV1Country } : undefined, { shouldValidate: true });
    },
    [valueName, setValue],
  );

  return (
    <VerticalStack gap={7}>
      <AppCheckboxInputWithLens
        label={
          <Typography lineHeight="1.125rem" size="0.875rem">
            This individual is a{' '}
            <Typography lineHeight="1.125rem" size="0.875rem" weight="bold">
              Politically Exposed Person
            </Typography>
            , holding a key public role in a non-US country.
          </Typography>
        }
        lens={lens.focus('isProviding')}
        onChange={handleAffiliatedPoliticallyExposedPersonToggle}
      />

      {isProviding ? <AppSelectInputWithLens label="Country" lens={lens.focus('value.country').defined()} options={countryOptions} required /> : null}
    </VerticalStack>
  );
}
